import { z } from 'zod';

import { t } from '../i18n/validation.js';

export type DeviceWhitelistForm = {
  entries: Array<{
    startFrequency: number;
    endFrequency: number;
  }>;
};

export const buildWhitelistSchema = (
  minAllowedMhz: number | null,
  maxAllowedMhz: number | null,
) => {
  const finiteNumber = (message: string) =>
    z.coerce.number({ error: message }).refine(Number.isFinite, message);

  let startFrequencyRangeSchema = finiteNumber(t('validation.frequency.startInvalid'));
  let endFrequencyRangeSchema = finiteNumber(t('validation.frequency.endInvalid'));

  if (minAllowedMhz !== null && minAllowedMhz > 0) {
    startFrequencyRangeSchema = startFrequencyRangeSchema.refine(
      (value: number) => value >= minAllowedMhz,
      {
        message: t('validation.frequency.startMin', { min: minAllowedMhz }),
      },
    );
    endFrequencyRangeSchema = endFrequencyRangeSchema.refine(
      (value: number) => value >= minAllowedMhz,
      {
        message: t('validation.frequency.endMin', { min: minAllowedMhz }),
      },
    );
  }

  if (maxAllowedMhz !== null && maxAllowedMhz > 0) {
    startFrequencyRangeSchema = startFrequencyRangeSchema.refine(
      (value: number) => value <= maxAllowedMhz,
      {
        message: t('validation.frequency.startMax', { max: maxAllowedMhz }),
      },
    );
    endFrequencyRangeSchema = endFrequencyRangeSchema.refine(
      (value: number) => value <= maxAllowedMhz,
      {
        message: t('validation.frequency.endMax', { max: maxAllowedMhz }),
      },
    );
  }

  return z.object({
    entries: z
      .array(
        z
          .object({
            startFrequency: startFrequencyRangeSchema,
            endFrequency: endFrequencyRangeSchema,
          })
          .superRefine((value, ctx) => {
            if (value.startFrequency >= value.endFrequency) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['endFrequency'],
                message: t('validation.frequency.endGreaterThanStart'),
              });
            }
          }),
      )
      .min(0),
  });
};
